import { parseStationTime, toStationWall } from './time';

const DAY_MS = 86400e3;
const J2000 = 2451545.0;
const UNIX_EPOCH_JD = 2440587.5;
const rad = (d) => (d * Math.PI) / 180;
const deg = (r) => (r * 180) / Math.PI;
const msToJd = (ms) => ms / DAY_MS + UNIX_EPOCH_JD;
const jdToMs = (jd) => (jd - UNIX_EPOCH_JD) * DAY_MS;

/**
 * Sunrise / sunset (ms UTC) for the solar day whose transit is nearest `noonMs`,
 * using the standard sunrise equation (NOAA/Wikipedia form, ±2 min). Sun centre at
 * -0.833° accounts for refraction and the solar radius.
 * Returns { sunrise: null, sunset: null } for polar day / polar night.
 */
export function sunTimes(noonMs, lat, lng) {
    const n = Math.round(msToJd(noonMs) - J2000);
    const jStar = n + 0.0008 - lng / 360; // longitude east-positive (mean solar noon is earlier in UTC further east)
    const M = (357.5291 + 0.98560028 * jStar) % 360;
    const C = 1.9148 * Math.sin(rad(M)) + 0.02 * Math.sin(rad(2 * M)) + 0.0003 * Math.sin(rad(3 * M));
    const lambda = (M + C + 180 + 102.9372) % 360;
    const jTransit = J2000 + jStar + 0.0053 * Math.sin(rad(M)) - 0.0069 * Math.sin(rad(2 * lambda));
    const decl = Math.asin(Math.sin(rad(lambda)) * Math.sin(rad(23.4397)));
    const cosOmega = (Math.sin(rad(-0.833)) - Math.sin(rad(lat)) * Math.sin(decl)) / (Math.cos(rad(lat)) * Math.cos(decl));
    if (cosOmega > 1 || cosOmega < -1) return { sunrise: null, sunset: null };
    const omega = deg(Math.acos(cosOmega));
    return { sunrise: jdToMs(jTransit - omega / 360), sunset: jdToMs(jTransit + omega / 360) };
}

/** Station-local (Belgrade) noon of the calendar day containing `ms`, shifted by `dayOffset` days. */
export function stationNoon(ms, dayOffset = 0) {
    const day = toStationWall(ms + dayOffset * DAY_MS, 'YYYY-MM-DD');
    return parseStationTime(`${day} 12:00:00`);
}

/**
 * Night intervals (sunset → next sunrise) overlapping [fromMs, toMs], clipped to it.
 * Returns [] when coordinates are missing or the sun never sets/rises there.
 */
export function nightBands(fromMs, toMs, lat, lng) {
    const la = Number(lat); const lo = Number(lng);
    if (!Number.isFinite(la) || !Number.isFinite(lo) || !(toMs > fromMs)) return [];
    const bands = [];
    const days = Math.ceil((toMs - fromMs) / DAY_MS) + 2;
    for (let i = -1; i < days; i++) {
        const { sunset } = sunTimes(stationNoon(fromMs, i), la, lo);
        const { sunrise } = sunTimes(stationNoon(fromMs, i + 1), la, lo);
        if (sunset === null || sunrise === null) continue;
        const from = Math.max(sunset, fromMs); const to = Math.min(sunrise, toMs);
        if (to > from) bands.push({ from, to });
    }
    return bands;
}
